import {
    getWebserviceProject,
    WebserviceProjectResponse,
} from '@yandex-int/k-common/client/api/defs/get-webservice-project';
import { postUploadWebserviceImage } from '@yandex-int/k-common/client/api/defs/post-upload-webservice-image';
import {
    postWebserviceSaveProject,
    WebserviceSavedAnswerLink,
} from '@yandex-int/k-common/client/api/defs/post-webservice-save-project';
import { forkFn } from '@yandex-lego/components/lib/forkFn';
import { produce } from 'immer';
import { sortBy } from 'lodash';
import { useQuery, QueryConfig, useMutation, queryCache, MutateConfig } from 'react-query';

import { useMe } from '../../utils/hooks/useMe';

import { ProblemWebServiceMarkup } from './types';

const getWebserviceProjectQueryKey = (projectName: string, meId: number | undefined) => [
    'get-webservice-project',
    projectName,
    meId,
];

export function useWebserviceProjectQuery(
    markup: ProblemWebServiceMarkup,
    queryConfig?: QueryConfig<WebserviceProjectResponse>
) {
    const myId = useMe('id');

    const key = getWebserviceProjectQueryKey(markup.project_name, myId);

    return useQuery<WebserviceProjectResponse>(
        key,
        () => {
            if (myId === undefined) {
                // Use fallback mock data when user is not authorized
                // to avoid breaking the UI
                return {
                    files: Object.fromEntries(markup.available_files.map((file) => [file, ''] as const)),
                    images: [],
                    latest_answer: {
                        files: {},
                    },
                };
            }

            return getWebserviceProject(markup.project_name);
        },
        {
            enabled: Boolean(markup.project_name),
            staleTime: Infinity,
            ...queryConfig,
        }
    );
}

type UploadImageArgs = Parameters<typeof postUploadWebserviceImage>;

export function useUploadImageMutation() {
    const myId = useMe('id');
    return useMutation((args: UploadImageArgs) => postUploadWebserviceImage(...args), {
        onSuccess: (uploadedImage, args) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [file, fileName, project] = args;
            const queryKey = getWebserviceProjectQueryKey(project, myId);
            const data = queryCache.getQueryData(queryKey) as WebserviceProjectResponse;
            queryCache.setQueryData(
                queryKey,
                produce(data, (draft) => {
                    draft.images.push(uploadedImage);
                    draft.images = sortBy(draft.images, 'name');
                })
            );
            queryCache.invalidateQueries(queryKey).catch(console.error);
        },
    });
}

type SaveProjectArgs = Parameters<typeof postWebserviceSaveProject>;

export function useSaveProjectMutation(config: MutateConfig<WebserviceSavedAnswerLink>) {
    const myId = useMe('id');
    return useMutation((args: SaveProjectArgs) => postWebserviceSaveProject(...args), {
        ...config,
        onSuccess: forkFn(config.onSuccess, (savedAnswerLink, args) => {
            const [project, files] = args;
            const queryKey = getWebserviceProjectQueryKey(project, myId);
            const data = queryCache.getQueryData(queryKey) as WebserviceProjectResponse;
            queryCache.setQueryData(
                queryKey,
                produce(data, (draft) => {
                    draft.latest_answer = {
                        files,
                    };
                })
            );
            queryCache.invalidateQueries(queryKey).catch(console.error);
        }),
    });
}
